import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { validateLoginIdentifier } from '../utils/contactFieldValidation';
import './Auth.css';

const VIEWS = {
  LOGIN: 'login',
  FORGOT_REQUEST: 'forgot-request',
  FORGOT_VERIFY: 'forgot-verify',
  FORGOT_RESET: 'forgot-reset',
};

const Auth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const fromState = location.state && location.state.from;

  const [view, setView] = useState(VIEWS.LOGIN);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSuccess = () => {
    if (fromState && fromState.pathname) {
      const target = `${fromState.pathname || '/'}${fromState.search || ''}${fromState.hash || ''}`;
      navigate(target, { replace: true });
    } else {
      navigate('/dashboard', { replace: true });
    }
  };

  const resetMessages = () => {
    setError('');
    setInfo('');
  };

  const switchView = (nextView) => {
    resetMessages();
    setView(nextView);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    resetMessages();

    const identifierCheck = validateLoginIdentifier(email, { required: true });
    if (!identifierCheck.ok) {
      setError(identifierCheck.message);
      return;
    }

    setLoading(true);
    const result = await login(identifierCheck.value, password);
    if (result.success) {
      handleSuccess();
    } else {
      setError(result.message);
    }
    setLoading(false);
  };

  const handleForgotRequest = async (e) => {
    e.preventDefault();
    resetMessages();

    const identifierCheck = validateLoginIdentifier(email, { required: true });
    if (!identifierCheck.ok) {
      setError(identifierCheck.message);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/auth/forgot-password/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: identifierCheck.value }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Unable to send verification code.');
      }
      setInfo(data.message);
      setVerificationCode('');
      setResetToken('');
      setNewPassword('');
      setConfirmPassword('');
      setView(VIEWS.FORGOT_VERIFY);
    } catch (requestError) {
      setError(requestError.message || 'Unable to send verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotVerify = async (e) => {
    e.preventDefault();
    resetMessages();
    setLoading(true);

    try {
      const response = await fetch('/api/auth/forgot-password/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: verificationCode.trim() }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Unable to verify code.');
      }
      setResetToken(data.token);
      setInfo('Code verified. Choose a new password below.');
      setView(VIEWS.FORGOT_RESET);
    } catch (verifyError) {
      setError(verifyError.message || 'Unable to verify code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotReset = async (e) => {
    e.preventDefault();
    resetMessages();

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, newPassword }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Unable to reset password.');
      }
      setInfo('Password updated successfully. You can sign in with your new password.');
      setPassword('');
      setVerificationCode('');
      setNewPassword('');
      setConfirmPassword('');
      setResetToken('');
      setView(VIEWS.LOGIN);
    } catch (resetError) {
      setError(resetError.message || 'Unable to reset password. Please request a new code.');
    } finally {
      setLoading(false);
    }
  };

  const renderMessages = () => (
    <>
      {error && (
        <div className="auth-message auth-message--error" role="alert">
          {error}
        </div>
      )}
      {info && (
        <div
          className={`auth-message ${
            view === VIEWS.LOGIN && info.includes('successfully')
              ? 'auth-message--success'
              : 'auth-message--info'
          }`}
        >
          {info}
        </div>
      )}
    </>
  );

  const renderBrand = (title, subtitle) => (
    <header className="auth-header">
      <p className="auth-eyebrow">Account</p>
      <h1 className="auth-title">{title}</h1>
      {subtitle && <p className="auth-lead">{subtitle}</p>}
    </header>
  );

  return (
    <div className="auth-page">
      <div className="auth-shell">
        <div className="auth-card">
          {view === VIEWS.LOGIN && (
            <>
              {renderBrand('Welcome back', 'Sign in to access your workspace.')}
              {renderMessages()}
              <form onSubmit={handleLogin} className="auth-form">
                <div className="auth-field">
                  <label htmlFor="auth-email" className="auth-label">
                    Email
                  </label>
                  <input
                    type="text"
                    id="auth-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="auth-input"
                    autoComplete="username"
                    required
                  />
                </div>
                <div className="auth-field">
                  <label htmlFor="auth-password" className="auth-label">
                    Password
                  </label>
                  <input
                    type="password"
                    id="auth-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="auth-input"
                    autoComplete="current-password"
                    required
                  />
                </div>
                <div className="auth-actions">
                  <button type="submit" className="auth-button" disabled={loading}>
                    {loading ? 'Signing in…' : 'Sign in'}
                  </button>
                </div>
              </form>
            </>
          )}

          {view === VIEWS.FORGOT_REQUEST && (
            <>
              {renderBrand(
                'Reset password',
                'Enter your account email and we will send a verification code.'
              )}
              {renderMessages()}
              <form onSubmit={handleForgotRequest} className="auth-form">
                <div className="auth-field">
                  <label htmlFor="forgot-email" className="auth-label">
                    Email
                  </label>
                  <input
                    type="email"
                    id="forgot-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="auth-input"
                    autoComplete="email"
                    required
                  />
                </div>
                <div className="auth-actions">
                  <button type="submit" className="auth-button" disabled={loading}>
                    {loading ? 'Sending code…' : 'Send verification code'}
                  </button>
                  <button
                    type="button"
                    className="auth-link-button"
                    onClick={() => switchView(VIEWS.LOGIN)}
                    disabled={loading}
                  >
                    Back to sign in
                  </button>
                </div>
              </form>
              <p className="auth-meta">Codes expire after 5 minutes.</p>
            </>
          )}

          {view === VIEWS.FORGOT_VERIFY && (
            <>
              {renderBrand(
                'Enter verification code',
                `Check your inbox for a code sent to ${email || 'your email'}.`
              )}
              {renderMessages()}
              <form onSubmit={handleForgotVerify} className="auth-form">
                <div className="auth-field">
                  <label htmlFor="verification-code" className="auth-label">
                    Verification code
                  </label>
                  <input
                    type="text"
                    id="verification-code"
                    value={verificationCode}
                    onChange={(e) =>
                      setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                    }
                    className="auth-input auth-input--code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="000000"
                    maxLength={6}
                    required
                  />
                </div>
                <div className="auth-actions">
                  <button
                    type="submit"
                    className="auth-button"
                    disabled={loading || verificationCode.length !== 6}
                  >
                    {loading ? 'Verifying…' : 'Verify code'}
                  </button>
                  <div className="auth-footer-row">
                    <button
                      type="button"
                      className="auth-link-button"
                      onClick={() => switchView(VIEWS.FORGOT_REQUEST)}
                      disabled={loading}
                    >
                      Resend code
                    </button>
                    <button
                      type="button"
                      className="auth-link-button"
                      onClick={() => switchView(VIEWS.LOGIN)}
                      disabled={loading}
                    >
                      Back to sign in
                    </button>
                  </div>
                </div>
              </form>
              <p className="auth-meta">
                Didn&apos;t receive a code? Password reset may not be available for your account,
                or the code may have expired.
              </p>
            </>
          )}

          {view === VIEWS.FORGOT_RESET && (
            <>
              {renderBrand('Choose a new password', 'Create a new password for your account.')}
              {renderMessages()}
              <form onSubmit={handleForgotReset} className="auth-form">
                <div className="auth-field">
                  <label htmlFor="new-password" className="auth-label">
                    New password
                  </label>
                  <input
                    type="password"
                    id="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="auth-input"
                    autoComplete="new-password"
                    required
                  />
                </div>
                <div className="auth-field">
                  <label htmlFor="confirm-password" className="auth-label">
                    Confirm password
                  </label>
                  <input
                    type="password"
                    id="confirm-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="auth-input"
                    autoComplete="new-password"
                    required
                  />
                </div>
                <div className="auth-actions">
                  <button type="submit" className="auth-button" disabled={loading}>
                    {loading ? 'Updating password…' : 'Update password'}
                  </button>
                  <button
                    type="button"
                    className="auth-link-button"
                    onClick={() => switchView(VIEWS.LOGIN)}
                    disabled={loading}
                  >
                    Back to sign in
                  </button>
                </div>
              </form>
            </>
          )}
        </div>

        {view === VIEWS.LOGIN && (
          <div className="auth-below-card">
            <button
              type="button"
              className="auth-forgot-link"
              onClick={() => switchView(VIEWS.FORGOT_REQUEST)}
            >
              Forgot password?
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Auth;
